import type { App, Directive, DirectiveBinding } from 'vue'
import { usePermissionStore } from '@/store/modules/permission'

type PowerValue = string | { permissions: string[]; logic?: 'OR' | 'AND' }

/** 兩種寫法都支援：`v-hasPower="'A_Add'"` 與 `v-hasPower="{ permissions: [...], logic: 'AND' }"` */
function normalize(value: PowerValue): { permissions: string[]; logic: 'OR' | 'AND' } {
  return typeof value === 'object' && value?.permissions
    ? { permissions: value.permissions, logic: value.logic ?? 'OR' }
    : { permissions: [value as string], logic: 'OR' }
}

/**
 * 依目前權限決定顯示與否。
 *
 * ⚠ 刻意用 `display` 隱藏，**不是** `el.remove()`：元素一旦被移除就脫離 DOM，
 * 之後權限才更新（登入後才拿到清單、或被 keep-alive 快取住的頁面重新啟用）也救不回來，
 * 症狀是「明明有權限，按鈕就是不出現」。留在 DOM 裡才能於 `updated` 時再切回來。
 *
 * 這只是畫面呈現，實際管控在後端 `FeatureAuthorize`——隱藏的按鈕就算被手動改回顯示，
 * 呼叫端點一樣會被擋，所以不需要為了防手動修改而堅持移除節點。
 */
function apply(el: HTMLElement, binding: DirectiveBinding<PowerValue>) {
  const { permissions: required, logic } = normalize(binding.value)
  const owned = usePermissionStore().getPermissions

  const allowed =
    logic === 'AND'
      ? required.every((code) => owned.includes(code))
      : required.some((code) => owned.includes(code))

  // 空字串＝清掉這條 inline style，交還給原本的 CSS 決定
  el.style.display = allowed ? '' : 'none'
}

const permiDirective: Directive = {
  mounted: apply,
  updated: apply
}

export const setupPowerCheckDirective = (app: App<Element>) => {
  app.directive('hasPower', permiDirective)
}

export default permiDirective
